/** Pure labels for the assessment status chip and the board glyph. */
import type { AssessmentStatus, AssessmentSummary } from '@/features/pipeline/types';

export interface AssessmentStatusMeta {
  label: string;
  /** Tailwind classes for the chip. */
  chip: string;
  /** One line under the chip explaining what happens next. */
  hint: string;
}

export function assessmentStatusMeta(status: AssessmentStatus): AssessmentStatusMeta {
  switch (status) {
    case 'sent':
      return { label: 'Sent', chip: 'bg-info-blue-bg text-info-blue', hint: 'The link was texted. It stays valid until the candidate completes it.' };
    case 'opened':
      // Dark text on the amber tint: `text-warning` on `bg-warning-bg` is ~3.5:1, under AA for 12 px.
      return { label: 'Opened', chip: 'bg-warning-bg text-gray-800', hint: 'The candidate opened the link but has not finished.' };
    case 'completed':
      return { label: 'Completed', chip: 'bg-success-bg text-success', hint: 'Results will show here once the instrument is live.' };
    default:
      return { label: 'Not sent', chip: 'bg-page text-gray-700', hint: 'Send the 5-minute work-style assessment by text.' };
  }
}

/** The status the UI should show: `not_sent` when the worker sent nothing. */
export function assessmentStatusOf(a: AssessmentSummary | null | undefined): AssessmentStatus {
  return a?.status ?? 'not_sent';
}

/** Short accessible name for the card glyph; `null` means "no glyph". */
export function assessmentGlyphLabel(status: AssessmentStatus | null | undefined): string | null {
  switch (status) {
    case 'sent':
      return 'Assessment sent';
    case 'opened':
      return 'Assessment opened';
    case 'completed':
      return 'Assessment completed';
    default:
      return null;
  }
}
